import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PageHeader from '@features/PageHeader';
import { CardsGrid } from '@components/CardItem/styles';
import Button from '@components/Button';
import { ModalConfirm } from '@components/Modal';
import { useTranslation } from '@hooks';
import { alertActions, channelsActions, agentsActions } from '@actions';
import { agentsService } from '@services';
import { handleApiError } from '@helpers/api';
import { VIEW_TYPE } from '@constants/surveys';
import usePermissions from '@hooks/usePermissions';
import { AgentsIcon } from 'assets/images/icons';
import AgentItem from './AgentItem';
import EditModal from './EditModal';

const Agents = () => {
    const { t } = useTranslation();

    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modal, setModal] = useState({ open: false, type: VIEW_TYPE.EDIT });

    const data = useSelector(state => state.agents.data);
    const dispatch = useDispatch();
    const selectedAgentRef = useRef(null);

    const { hasFullAccess } = usePermissions();
    const canManageAgents = hasFullAccess('messaging');
    const canUsePlayground = hasFullAccess('messaging');

    useEffect(() => {
        dispatch(agentsActions.get());
        dispatch(channelsActions.get());
    }, []);

    const onCloseEditModal = () => {
        setModal({ open: false, type: VIEW_TYPE.EDIT });
        selectedAgentRef.current = null;
    };

    const onClickCard = async (agent, type) => {
        selectedAgentRef.current = agent;
        switch (type) {
            case 'toggleStatus':
                await onToggleStatus();
                break;
            case 'edit':
                setModal({ open: true, type: VIEW_TYPE.EDIT });
                break;
            case 'view':
                setModal({ open: true, type: VIEW_TYPE.VIEW });
                break;
            case 'delete':
                setDeleteModalOpen(true);
                break;
            default:
                break;
        }
    };

    const onToggleStatus = async () => {
        try {
            setIsSubmitting(true);
            const updatedAgent = await agentsService.updateStatusAgent(
                selectedAgentRef.current?.id,
                !!selectedAgentRef.current?.status,
            );
            const agentsCopy = data.slice();
            const updatedAgentIndex = agentsCopy.findIndex(c => c.id === selectedAgentRef.current?.id);
            if (updatedAgentIndex !== -1) {
                agentsCopy[updatedAgentIndex].status = updatedAgent.status;
                dispatch(agentsActions.update(agentsCopy));
            }
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail');
        } finally {
            setIsSubmitting(false);
        }
    };

    const onDelete = async () => {
        try {
            await agentsService.deleteAgent(selectedAgentRef.current?.id);
            await dispatch(agentsActions.get());
            await dispatch(channelsActions.get());
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            setDeleteModalOpen(false);
            if (modal.open) {
                setModal({ open: false, type: VIEW_TYPE.EDIT });
            }
            selectedAgentRef.current = null;
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail');
        }
    };

    return (
        <>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<AgentsIcon />}
                title={t('Agents.title')}
                subtitle={t('Agents.subtitle')}
            >
                {canManageAgents && (
                    <Button variant="contained" onClick={() => setModal({ open: true, type: VIEW_TYPE.EDIT })}>
                        {t('buttons.addAgent')}
                    </Button>
                )}
            </PageHeader>
            <CardsGrid sx={{ paddingBottom: '16px' }}>
                {data.map(item => (
                    <AgentItem
                        key={item.id}
                        data={item}
                        canWrite={canManageAgents}
                        isSubmitting={isSubmitting}
                        onClickCard={onClickCard}
                    />
                ))}
            </CardsGrid>
            {modal.open ? (
                <EditModal
                    modal={modal}
                    onClose={onCloseEditModal}
                    canWrite={canManageAgents}
                    canUsePlayground={canUsePlayground}
                    selectedAgentRef={selectedAgentRef}
                    onDelete={() => setDeleteModalOpen(true)}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </>
    );
};

export default Agents;
